// =============================================================================
// FamilyU child-theme frontend behaviours (vanilla JS, no dependencies).
// Implements EXACTLY the Shared Contract JS hooks so the block markup emitted by
// the C1–C7 agents wires up automatically:
//   1. Shop mega-menu toggle  (window capture-phase outside-close + Escape +
//                              link close; the fixed click-catcher is inert CSS)
//   2. Mobile off-canvas drawer (slide-in panel + backdrop + body scroll-lock +
//                              focus move/restore/trap + accordion submenu)
//   3. Header search          (native GET submit to /shop/?q=, debounced auto-go)
//   4. Shop client-side filter (cat / stage / brand / ?q= over product cards,
//                              two-way sync with the ?cat / ?stage / ?brand=
//                              deep links so category links land pre-filtered)
//   5. Cart feedback          (demo toast + header badge + a re-skinned parent
//                              toast carrying our "View cart" action)
//
// SINGLE NOTIFICATION PER ADD (review #2): a real add is written by the parent
// theme's Store API module, which also shows ITS OWN `.az-toast`. We therefore
// do not emit a child toast for it — we re-skin the parent's (see
// src/scss/_toast-parent.scss) and augment it with a cart link. `.fu-toast` is
// still ours and still used by the 22 demo cards, which have no parent
// equivalent.
//
// jQuery is ONLY touched through a guarded bridge (see onWcAddedToCart) because
// WooCommerce's `added_to_cart` is a jQuery event on document.body. Nothing here
// throws when jQuery / WooCommerce are absent.
// =============================================================================

// --- User-facing copy --------------------------------------------------------
// Review #5: every string now comes from the server instead of a hardcoded
// English constant. functions.php does
//   wp_localize_script('ai-zippy-child', 'fuI18n', [...])
// with values read through `__()` / `_n()` in the 'ai-zippy' domain, plus
// `wp_set_script_translations()` for the JS side. WP prints that payload as an
// inline CLASSIC script immediately before our `type="module"` tag, and module
// scripts are deferred, so `window.fuI18n` is always populated by the time this
// file evaluates.
//
// The literals below are the en_US source strings and are used ONLY if the
// payload is missing (page cache from before the localize call, or the script
// loaded some other way). They are the same strings the server passes to `__()`,
// so a translation catalog still matches.
const fuI18n = (typeof window !== 'undefined' && window.fuI18n) || {};

const fuStr = (key, fallback) => {
  const value = fuI18n[key];
  return typeof value === 'string' && value !== '' ? value : fallback;
};

const FU_DEMO_CART_MESSAGE = fuStr(
  'demo',
  "This is a demo — the cart isn't wired for this product."
);
const FU_ADDED_TO_CART_MESSAGE = fuStr('added', 'Added to cart');
const FU_VIEW_CART_LABEL = fuStr('viewCart', 'View cart');
const FU_TOAST_DISMISS_LABEL = fuStr('dismiss', 'Dismiss notification');

// Accessible name of the hamburger disclosure (Task #48 / O1). The button is
// icon-only, so `aria-label` is its name; it has to say what the button will DO,
// which changes with the drawer state. render.php ships "Open menu" server-side
// and the drawer controller below rewrites both attributes together.
const FU_MENU_OPEN_LABEL = fuStr('menuOpen', 'Open menu');
const FU_MENU_CLOSE_LABEL = fuStr('menuClose', 'Close menu');

// Plural forms for the sr-only cart label, so JS rebuilds the SAME translated
// string the server rendered with `_n()` instead of overwriting it in English.
const FU_CART_LABEL_ONE = fuStr('cartLabelOne', '%s item in cart');
const FU_CART_LABEL_MANY = fuStr('cartLabelMany', '%s items in cart');

const FU_TOAST_DURATION = 4000; // ms — "auto-dismiss ~4s"
const FU_TOAST_MIN_DURATION = 1000;
const FU_TOAST_EXIT_MS = 300; // must match --fu-toast-exit in _toast.scss

const FU_CART_COUNT_ATTR = 'data-fu-cart-count';
const FU_CART_COUNT_LABEL_ATTR = 'data-fu-cart-count-label';
const FU_CART_URL_ATTR = 'data-fu-cart-url';

// --- Cart URL (review #6) ----------------------------------------------------
// Resolution order:
//   1. `fuI18n.cartUrl`  — PHP `wc_get_cart_url()`, authoritative.
//   2. `[data-fu-cart-url]` on the header cart link — same value, but survives a
//      page where the localize payload was stripped by a cache.
//   3. `/cart/` — last resort only, so the action is never a dead link.
const FU_CART_URL_FALLBACK = '/cart/';
let fuCartUrlCache = '';

const fuCartUrl = () => {
  if (fuCartUrlCache) return fuCartUrlCache;

  let url = typeof fuI18n.cartUrl === 'string' ? fuI18n.cartUrl.trim() : '';

  if (!url) {
    const holder = document.querySelector(`[${FU_CART_URL_ATTR}]`);
    url = holder ? (holder.getAttribute(FU_CART_URL_ATTR) || '').trim() : '';
  }

  fuCartUrlCache = url || FU_CART_URL_FALLBACK;
  return fuCartUrlCache;
};

// --- Number handling ---------------------------------------------------------
// The badge and the Store API payload can both carry locale-formatted digits
// ("1,000"), which `parseInt` silently truncates to 1. Strip to digits first,
// and format on the way out with the document's own locale so what we write
// matches `number_format_i18n()` on the server.
const fuDigits = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
  }
  const digits = String(value === null || value === undefined ? '' : value).replace(/[^\d]/g, '');
  const parsed = parseInt(digits, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

let fuNumberFormat;
const fuFormatCount = (n) => {
  if (fuNumberFormat === undefined) {
    try {
      const lang = (document.documentElement.getAttribute('lang') || '').replace('_', '-') || undefined;
      fuNumberFormat = new Intl.NumberFormat(lang, { maximumFractionDigits: 0 });
    } catch (err) {
      fuNumberFormat = null; // older engine / bad BCP-47 tag — plain digits then
    }
  }
  if (!fuNumberFormat) return String(n);
  try {
    return fuNumberFormat.format(n);
  } catch (err) {
    return String(n);
  }
};

/**
 * Substitute into a gettext plural template (`%s` or `%1$s`).
 *
 * A replacer FUNCTION is used deliberately: with a string replacement `$`-led
 * sequences in the value would be interpreted as special patterns.
 */
const fuSubstitute = (template, value) =>
  String(template).replace(/%(?:\d+\$)?[sd]/g, () => value);

// =============================================================================
// Toast — ONE reusable fixed-position element (never stacks unbounded).
//
// The element is created empty and kept in the DOM permanently so screen
// readers see it as a live region BEFORE its content changes; appending a fresh
// node per message would be silently ignored by AT. It is mounted on
// document.body on purpose: `.fu-site-header` is `position: sticky`, and a
// containing-block-creating property there (backdrop-filter / filter /
// transform / will-change / contain) would trap any position:fixed descendant
// inside the header box. None is present today — see the WARNING in
// src/blocks/shared-header-main/style.scss — and mounting on body keeps the
// toast correct even if one is ever reintroduced.
// =============================================================================

let fuToastEl = null;
let fuToastHideTimer = 0;
let fuToastClearTimer = 0;

const fuEnsureToast = () => {
  if (fuToastEl && fuToastEl.isConnected) return fuToastEl;

  const toast = document.createElement('div');
  toast.className = 'fu-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.setAttribute('aria-atomic', 'true');
  (document.body || document.documentElement).appendChild(toast);
  fuToastEl = toast;
  return toast;
};

const fuHideToast = () => {
  clearTimeout(fuToastHideTimer);
  fuToastHideTimer = 0;
  if (!fuToastEl) return;

  fuToastEl.classList.remove('is-visible');

  // Blank the message only once the exit transition has finished, so a stale
  // message can never be re-announced by the live region.
  clearTimeout(fuToastClearTimer);
  fuToastClearTimer = setTimeout(() => {
    if (fuToastEl && !fuToastEl.classList.contains('is-visible')) {
      fuToastEl.textContent = '';
    }
  }, FU_TOAST_EXIT_MS);
};

/**
 * Show (or replace) the single toast.
 *
 * @param {string} message              Plain-text message.
 * @param {Object} [options]
 * @param {string} [options.actionLabel] Optional action link text.
 * @param {string} [options.actionHref]  Optional action link URL.
 * @param {string} [options.tone]        'success' | 'warn' | 'info'.
 * @param {number} [options.duration]    Auto-dismiss delay in ms.
 */
const fuShowToast = (message, options = {}) => {
  const {
    actionLabel = '',
    actionHref = '',
    tone = 'info',
    duration = FU_TOAST_DURATION,
  } = options || {};

  const toast = fuEnsureToast();
  const life = Math.max(FU_TOAST_MIN_DURATION, Number(duration) || FU_TOAST_DURATION);

  clearTimeout(fuToastHideTimer);
  clearTimeout(fuToastClearTimer);

  // Rebuild in place: one element, so rapid clicks replace rather than stack.
  toast.textContent = '';
  toast.dataset.tone = tone;
  toast.style.setProperty('--fu-toast-life', `${life}ms`);

  const icon = document.createElement('span');
  icon.className = 'fu-toast__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = tone === 'success' ? '\u2713' : tone === 'warn' ? '!' : '\u2022';
  toast.appendChild(icon);

  const text = document.createElement('span');
  text.className = 'fu-toast__text';
  text.textContent = String(message);
  toast.appendChild(text);

  if (actionLabel && actionHref) {
    const action = document.createElement('a');
    action.className = 'fu-toast__action';
    action.href = actionHref;
    action.textContent = actionLabel;
    toast.appendChild(action);
  }

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'fu-toast__dismiss';
  dismiss.setAttribute('aria-label', FU_TOAST_DISMISS_LABEL);
  dismiss.textContent = '\u00d7';
  dismiss.addEventListener('click', fuHideToast);
  toast.appendChild(dismiss);

  const progress = document.createElement('span');
  progress.className = 'fu-toast__progress';
  progress.setAttribute('aria-hidden', 'true');
  toast.appendChild(progress);

  // Restart the entrance animation + progress bar on the reused element.
  toast.classList.remove('is-visible');
  void toast.offsetWidth; // forced reflow — required to replay a CSS animation
  toast.classList.add('is-visible');

  fuToastHideTimer = setTimeout(fuHideToast, life);
  return toast;
};

// =============================================================================
// Header cart-count badge — seeded from the server-rendered value, then bumped
// in place after each add (no extra fetch, no fragment round-trip).
// =============================================================================

const fuBadges = () => document.querySelectorAll(`[${FU_CART_COUNT_ATTR}]`);

const fuReadCartCount = (badge) => {
  const raw = badge.getAttribute(FU_CART_COUNT_ATTR) || badge.textContent || '0';
  return fuDigits(raw);
};

const fuSetCartCount = (count) => {
  const next = fuDigits(count);
  const shown = fuFormatCount(next);

  fuBadges().forEach((badge) => {
    badge.textContent = shown;
    // The attribute keeps RAW digits so fuReadCartCount() — and anything else
    // reading the hook — never has to undo locale grouping.
    badge.setAttribute(FU_CART_COUNT_ATTR, String(next));
    badge.classList.toggle('is-empty', next === 0);
  });

  // Review #5 — built from the localized plural strings. This used to write a
  // hardcoded English "N item(s) in cart" straight over the `_n()`-translated
  // label shared-header-main/render.php had just rendered.
  const label = fuSubstitute(next === 1 ? FU_CART_LABEL_ONE : FU_CART_LABEL_MANY, shown);
  document.querySelectorAll(`[${FU_CART_COUNT_LABEL_ATTR}]`).forEach((el) => {
    el.textContent = label;
  });
};

const fuBumpCartCount = (delta) => {
  const badge = fuBadges()[0];
  if (!badge) return;
  fuSetCartCount(fuReadCartCount(badge) + fuDigits(delta));
};

/**
 * Quantity actually added. WC passes the clicked button as the 4th
 * `added_to_cart` arg (jQuery object, DOM node, or nothing at all when a
 * plugin triggers the event manually) — every shape degrades to +1.
 */
const fuQuantityFrom = ($button) => {
  const node =
    $button instanceof Element ? $button : $button && $button[0] instanceof Element ? $button[0] : null;
  if (!node) return 1;
  const qty = fuDigits(node.getAttribute('data-quantity'));
  return qty > 0 ? qty : 1;
};

/**
 * Bind a WooCommerce `added_to_cart` handler without ever depending on jQuery
 * being present. jQuery + wc-add-to-cart are deferred classic scripts, so a
 * module script normally runs after them; `load` is the safety net.
 */
const fuOnWcAddedToCart = (handler) => {
  const bind = () => {
    if (!window.jQuery) return false;
    window.jQuery(document.body).on('added_to_cart', handler);
    return true;
  };
  if (bind()) return;
  window.addEventListener('load', bind);
};

/**
 * Cart payload -> total quantity.
 *
 * A Store API cart carries `items_count` (the sum of every line's quantity),
 * which is authoritative: SETTING the badge from it can neither drift nor
 * double-count, unlike incrementing. Falls back to summing `items[].quantity`,
 * then to `null` so the caller can degrade to a +1 bump.
 *
 * @param {Object|null} [cart] Store API cart object.
 * @return {number|null} Total quantity, or null when the payload is unusable.
 */
const fuCartCountFromPayload = (cart) => {
  if (!cart || typeof cart !== 'object') return null;

  const raw = cart.items_count;
  if (raw !== undefined && raw !== null && raw !== '') {
    // Only a digit-bearing string is trusted, so a junk value still falls
    // through to summing items[] rather than reporting a false 0.
    const parsed =
      typeof raw === 'number' ? raw : /\d/.test(String(raw)) ? fuDigits(raw) : NaN;
    if (Number.isFinite(parsed)) return Math.max(0, Math.round(parsed));
  }

  if (Array.isArray(cart.items)) {
    return cart.items.reduce((total, item) => total + fuDigits(item && item.quantity), 0);
  }

  return null;
};

// ONE Store API write publishes TWO success events: `wc-blocks_added_to_cart`
// (carrying the new cart) and then the jQuery `added_to_cart` (carrying
// nothing). The jQuery path is therefore suppressed for a short window after
// the authoritative one lands. A timestamp rather than a boolean, so an add
// that never produces the second event cannot leave the fallback stuck off.
let fuLastStoreAddAt = 0;
const FU_STORE_ADD_DEDUP_MS = 1500;

const fuStoreAddJustHandled = () => Date.now() - fuLastStoreAddAt < FU_STORE_ADD_DEDUP_MS;

const fuShowAddedToast = () =>
  fuShowToast(FU_ADDED_TO_CART_MESSAGE, {
    actionLabel: FU_VIEW_CART_LABEL,
    actionHref: fuCartUrl(),
    tone: 'success',
  });

// The parent theme's toast, built by `ai-zippy/src/js/frontend/modules/toast.js`
// and appended directly to document.body. It is the single notification for a
// real add (review #2) — see §5b/§5e and src/scss/_toast-parent.scss.
const FU_PARENT_TOAST = '.az-toast';
const fuParentToastActive = () => !!document.querySelector(FU_PARENT_TOAST);

document.addEventListener('DOMContentLoaded', () => {
  // --- 1. Shop mega-menu -----------------------------------------------------
  // Nodes are re-queried on every event instead of captured once here. If
  // anything ever replaces the header markup (a block re-render, a plugin, a
  // prerender swap) the handlers keep driving the LIVE nodes; cached references
  // would silently write to detached elements and the menu would look stuck
  // open even though closeShopMenu() ran.
  const fuToggle = () => document.querySelector('[data-fu-shop-toggle]');
  const fuMenu = () => document.querySelector('[data-fu-shop-menu]');
  const fuCatcher = () => document.querySelector('[data-fu-shop-catcher]');
  const fuDrawer = () => document.querySelector('[data-fu-mobile-nav]');
  const fuBurger = () => document.querySelector('[data-fu-hamburger]');

  const fuMenuIsOpen = () => {
    const menu = fuMenu();
    return !!menu && !menu.hidden;
  };

  const closeShopMenu = () => {
    const menu = fuMenu();
    const catcher = fuCatcher();
    const btn = fuToggle();

    // Review #7 — hiding the panel while focus is inside it drops focus to
    // <body>, so a keyboard user who pressed Escape lost their place entirely
    // and had to tab back from the top of the document. Capture WHERE focus was
    // before hiding (once `hidden` is set the element is no longer focusable and
    // activeElement has already moved), then hand focus back to the toggle that
    // owns the disclosure.
    //
    // Deliberately narrow: this only fires when focus is genuinely inside the
    // panel. Clicking the toggle leaves activeElement ON the toggle (outside the
    // panel), and an outside pointerdown leaves it on whatever was focused
    // before — neither case steals focus.
    const active = document.activeElement;
    const restoreFocus =
      !!btn &&
      !!menu &&
      !!active &&
      active !== document.body &&
      typeof menu.contains === 'function' &&
      menu.contains(active);

    if (menu) menu.hidden = true;
    if (catcher) catcher.hidden = true;
    if (btn) btn.setAttribute('aria-expanded', 'false');

    if (restoreFocus) {
      // preventScroll: the panel collapse already moves content, and yanking the
      // viewport to the header on top of it reads as a jump.
      try {
        btn.focus({ preventScroll: true });
      } catch (err) {
        btn.focus();
      }
    }
  };
  const openShopMenu = () => {
    const menu = fuMenu();
    const catcher = fuCatcher();
    const btn = fuToggle();
    if (menu) menu.hidden = false;
    if (catcher) catcher.hidden = false;
    if (btn) btn.setAttribute('aria-expanded', 'true');
  };
  const toggleShopMenu = () => (fuMenuIsOpen() ? closeShopMenu() : openShopMenu());

  // Task #46: the drawer is no longer a `hidden`-flipping dropdown. It delegates
  // to the off-canvas controller in §2, which owns the class/aria/scroll-lock
  // state. `fuSetMobileNav` is a hoisted function declaration, so this const can
  // reference it even though §2 appears further down the same scope — and the
  // Escape handler below can only ever fire after this whole body has run.
  const closeMobileNav = () => fuSetMobileNav(false);

  // An interaction that belongs to the menu itself — the toggle, or anything
  // inside the panel — must never be mistaken for an outside interaction.
  const fuIsOwnTarget = (target) =>
    !!target &&
    typeof target.closest === 'function' &&
    (!!target.closest('[data-fu-shop-menu]') || !!target.closest('[data-fu-shop-toggle]'));

  // The toggle is delegated on document rather than bound to the element, so it
  // survives a header re-render. It deliberately no longer calls
  // stopPropagation(): the outside handlers below exclude the toggle by
  // contract (fuIsOwnTarget), which removes the fragile coupling where the
  // close path depended on the toggle swallowing its own click before it could
  // reach document.
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-fu-shop-toggle]')) return;
    e.preventDefault();
    toggleShopMenu();
  });

  // Picking any link inside the panel closes the menu (the navigation itself is
  // left alone — that click is meant to go through).
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-fu-shop-menu] a')) return;
    closeShopMenu();
  });

  // The click-catcher stays wired as a defensive fallback only. It is
  // `pointer-events: none` in CSS on purpose (see shared-primary-nav/style.scss)
  // so it can never intercept a click aimed at real page content — a
  // full-viewport fixed element above the content makes every such click
  // untargetable, for assistive tech and automation alike. Should a third party
  // ever re-enable pointer events on it, this handler still closes the menu.
  const fuCatcherEl = fuCatcher();
  if (fuCatcherEl) fuCatcherEl.addEventListener('click', closeShopMenu);

  // ---- Outside interaction + Escape ----------------------------------------
  // Registered on `window` in the CAPTURE phase: the first stop of the
  // propagation path. Nothing bound later — on the target, on document, or in
  // the bubble phase, by this theme, the parent theme, WooCommerce or a plugin
  // — can stopPropagation() these away, and they no longer depend on the fixed
  // catcher winning the hit test.
  //
  // `pointerdown` is handled as well as `click` because a real browser fires
  // pointerdown even when a click never materialises (the target is replaced
  // between down and up, an overlay swallows the mouseup, a touch turns into a
  // scroll). That is what makes outside-close reliable on touch and under
  // automation.
  //
  // Closing on pointerdown would otherwise let the click that follows activate
  // whatever ended up underneath — an outside interaction should dismiss the
  // menu and do nothing else, which is the behaviour the catcher used to give
  // us. So that one click is consumed: preventDefault() cancels the navigation
  // and stopPropagation() in the capture phase stops it before it reaches the
  // target.
  //
  // The window is ARMED PER TARGET, not just per time (review #9). A plain
  // 500ms timer swallowed the next click no matter where it landed, so a
  // pointerdown that never produced a click — a drag, a touch that became a
  // scroll, a target swapped out between down and up — ate a completely
  // unrelated later click. Now the click is only consumed when its target IS
  // the armed pointerdown target (or lives inside it), and:
  //   * `pointercancel` disarms outright — no click will ever follow it;
  //   * `pointerup` cannot disarm (the click fires immediately AFTER it), so it
  //     NARROWS the window to a short settle grace instead.
  // Both are registered in the capture phase for the same reason as the others.
  let fuSwallowUntil = 0;
  let fuSwallowTarget = null;
  const FU_SWALLOW_MS = 500;
  const FU_SWALLOW_SETTLE_MS = 120;

  const fuDisarmSwallow = () => {
    fuSwallowUntil = 0;
    fuSwallowTarget = null;
  };

  const fuSwallowApplies = (target) => {
    if (Date.now() >= fuSwallowUntil) {
      // Expired: drop the node reference so a detached element can be collected.
      if (fuSwallowTarget) fuDisarmSwallow();
      return false;
    }
    if (!fuSwallowTarget || !target) return false;
    return (
      target === fuSwallowTarget ||
      (typeof fuSwallowTarget.contains === 'function' && fuSwallowTarget.contains(target))
    );
  };

  window.addEventListener(
    'pointerdown',
    (e) => {
      // Primary activation only. A right-click (button 2) produces no `click`
      // event, so arming the swallow window for one would leave the next real
      // click — the one the context menu just cancelled — eaten for nothing.
      if (e.button !== 0) return;
      if (!fuMenuIsOpen() || fuIsOwnTarget(e.target)) return;
      closeShopMenu();
      fuSwallowUntil = Date.now() + FU_SWALLOW_MS;
      fuSwallowTarget = e.target instanceof Element ? e.target : null;
    },
    true
  );

  window.addEventListener(
    'pointerup',
    () => {
      if (!fuSwallowTarget) return;
      fuSwallowUntil = Math.min(fuSwallowUntil, Date.now() + FU_SWALLOW_SETTLE_MS);
    },
    true
  );

  window.addEventListener('pointercancel', fuDisarmSwallow, true);

  window.addEventListener(
    'click',
    (e) => {
      // Review #8 — the own-target test comes FIRST. It used to run after the
      // swallow test, so clicking the toggle straight after an outside-dismiss
      // was eaten and reopening the menu needed two clicks.
      if (fuIsOwnTarget(e.target)) return;

      if (fuSwallowApplies(e.target)) {
        fuDisarmSwallow();
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (!fuMenuIsOpen()) return;
      closeShopMenu();
    },
    true
  );

  // 'Esc' is the legacy key value some engines still report.
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      // Close the top-most surface only, so one Escape press does one thing.
      if (fuMenuIsOpen()) closeShopMenu();
      else closeMobileNav();
    },
    true
  );

  // --- 2. Mobile off-canvas drawer (Task #46) --------------------------------
  // Was: flip `hidden` on a dropdown bolted under the header. Now: a fixed panel
  // that slides in from the right over a fading backdrop, printed by render.php at
  // wp_footer so no ancestor can trap it in a containing block or a stacking
  // context (see familyu_nav_mobile_panel()).
  //
  // State lives in THREE synchronised places, and every one of them is written by
  // fuSetMobileNav() and nowhere else:
  //   * `.is-open` on the panel + backdrop  → the CSS transform / opacity / visibility
  //   * `.fu-nav-open` on <body>            → the scroll-lock
  //   * `aria-expanded` + `aria-label` on the hamburger + `aria-hidden` on the panel
  //     → the a11y state
  //
  // Like §1, nodes are re-queried per event and handlers are delegated on
  // `document`, so a header re-render cannot leave these driving detached elements.
  const fuBackdrop = () => document.querySelector('[data-fu-mobile-backdrop]');
  const fuCloseBtn = () => document.querySelector('[data-fu-mobile-close]');

  // The hamburger's a11y state is TWO attributes, and they must never disagree:
  // `aria-expanded` for the disclosure state and `aria-label` for what pressing
  // it will do next (Task #48 / O1). Funnelled through one setter so no call site
  // can flip one and forget the other.
  const fuSetBurgerState = (burger, open) => {
    if (!burger) return;
    burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    burger.setAttribute('aria-label', open ? FU_MENU_CLOSE_LABEL : FU_MENU_OPEN_LABEL);
  };

  // Must stay ≥ --fu-drawer-speed (.28s) plus slack: `hidden` goes back on only
  // after the slide-out has finished, or the animation is cut at frame one.
  const FU_DRAWER_HIDE_MS = 320;
  let fuDrawerHideTimer = 0;

  // What Tab may land on inside the drawer. Kept in sync with the CSS: a link
  // inside a COLLAPSED accordion is `visibility: hidden` and therefore not
  // focusable, so it is filtered out by the `.is-open` test below rather than by a
  // layout query (`offsetParent` is null in jsdom, which would drop everything).
  const FU_DRAWER_FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const fuMobileNavIsOpen = () => {
    const panel = fuDrawer();
    return !!panel && panel.classList.contains('is-open');
  };

  // Resolved through aria-controls, never a hardcoded id, so a second accordion
  // added later needs no change here.
  const fuSubmenuFor = (btn) => {
    const id = btn && btn.getAttribute ? btn.getAttribute('aria-controls') : null;
    return id ? document.getElementById(id) : null;
  };

  const fuSetSubmenu = (btn, open) => {
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    const sub = fuSubmenuFor(btn);
    if (sub) sub.classList.toggle('is-open', !!open);
  };

  const fuIsFocusableInDrawer = (el) => {
    if (!el || el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    const sub = typeof el.closest === 'function' ? el.closest('[data-fu-mobile-submenu]') : null;
    return !sub || sub.classList.contains('is-open');
  };

  const fuFocusQuiet = (el) => {
    if (!el) return;
    // preventScroll: opening/closing already moves the viewport's content, and
    // yanking the scroll position on top of that reads as a jump.
    try {
      el.focus({ preventScroll: true });
    } catch (err) {
      el.focus();
    }
  };

  // Declared as a function (not a const) so §1's `closeMobileNav` above can reach
  // it through hoisting.
  function fuSetMobileNav(open) {
    const panel = fuDrawer();
    if (!panel) return;

    const backdrop = fuBackdrop();
    const burger = fuBurger();

    if (open) {
      window.clearTimeout(fuDrawerHideTimer);
      fuDrawerHideTimer = 0;

      panel.hidden = false;
      if (backdrop) backdrop.hidden = false;

      // Force the browser to commit `display: flex` at translateX(100%) BEFORE
      // `is-open` flips it to 0. Without this style flush both land in the same
      // frame, there is no starting value to interpolate from, and the panel
      // appears instead of sliding in.
      void panel.offsetWidth;

      panel.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      fuSetBurgerState(burger, true);
      // Body scroll-lock. `overflow: hidden` on <body> reaches the viewport
      // because <html> computes to `overflow: visible` and the UA propagates it.
      if (document.body) document.body.classList.add('fu-nav-open');

      // Focus moves INTO the panel, to the labelled close button, so a keyboard
      // user who opened the drawer is not left behind on the hamburger.
      fuFocusQuiet(fuCloseBtn());
      return;
    }

    // Closing when already closed must be a true no-op: §1's Escape handler calls
    // this on EVERY Escape press, and an unconditional focus-restore would yank
    // focus to the hamburger from anywhere on the page.
    if (!panel.classList.contains('is-open')) return;

    window.clearTimeout(fuDrawerHideTimer);

    panel.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    fuSetBurgerState(burger, false);
    if (document.body) document.body.classList.remove('fu-nav-open');

    // Collapse every accordion. A submenu left `.is-open` sets
    // `visibility: visible` on its inner, and visibility UN-inherits — so it would
    // keep its links focusable inside a panel that is itself `visibility: hidden`.
    // (The CSS also requires `.fu-mobile-nav.is-open` for that override; this is
    // the JS half of the same guarantee, and it makes aria-expanded honest.)
    panel.querySelectorAll('[data-fu-mobile-submenu-toggle]').forEach((btn) => {
      fuSetSubmenu(btn, false);
    });

    // Focus returns to the hamburger that owns the disclosure — for the close
    // button, Escape and backdrop paths alike, per the Task #46 contract.
    fuFocusQuiet(burger);

    // Put `hidden` back once the slide-out is done, so a page that loses its
    // stylesheet can never dump the drawer into the flow.
    fuDrawerHideTimer = window.setTimeout(() => {
      fuDrawerHideTimer = 0;
      if (fuMobileNavIsOpen()) return;
      panel.hidden = true;
      if (backdrop) backdrop.hidden = true;
    }, FU_DRAWER_HIDE_MS);
  }

  const fuOpenMobileNav = () => fuSetMobileNav(true);
  const fuToggleMobileNav = () => fuSetMobileNav(!fuMobileNavIsOpen());

  // Take over from the server-rendered `hidden` so the closed state is expressed
  // by CSS (translateX + visibility) and the first open can animate.
  (function fuMobileNavBoot() {
    const panel = fuDrawer();
    const backdrop = fuBackdrop();
    if (!panel) return;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'true');
    panel.classList.remove('is-open');
    if (backdrop) {
      backdrop.hidden = false;
      backdrop.classList.remove('is-open');
    }
    const burger = fuBurger();
    fuSetBurgerState(burger, false);
    if (document.body) document.body.classList.remove('fu-nav-open');
    // A reloaded page must never come back with a stuck-open accordion.
    panel.querySelectorAll('[data-fu-mobile-submenu-toggle]').forEach((btn) => {
      fuSetSubmenu(btn, false);
    });
  })();

  // ---- Hamburger ------------------------------------------------------------
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-fu-hamburger]')) return;
    e.preventDefault();
    fuToggleMobileNav();
  });

  // ---- Close button / backdrop ----------------------------------------------
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-fu-mobile-close]') && !target.closest('[data-fu-mobile-backdrop]')) {
      return;
    }
    e.preventDefault();
    fuSetMobileNav(false);
  });

  // ---- Any navigation link inside the panel closes it ------------------------
  // Deliberately NO preventDefault: the navigation is the whole point of the
  // click and must go through. The chevron is a <button>, not an <a>, so the
  // accordion is not caught by this.
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-fu-mobile-nav] a')) return;
    fuSetMobileNav(false);
  });

  // ---- Accordion submenu ----------------------------------------------------
  // Delegated, so it works for any number of independent accordions added later.
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    const btn = target.closest('[data-fu-mobile-submenu-toggle]');
    if (!btn) return;
    e.preventDefault();
    fuSetSubmenu(btn, btn.getAttribute('aria-expanded') !== 'true');
  });

  // ---- Focus trap ------------------------------------------------------------
  // Without `inert` on the rest of the document (not safe to add: it would also
  // disable the parent theme's cart/toast nodes) Tab would walk straight out of
  // the drawer into the page behind the backdrop. Registered in the capture phase
  // for the same reason as §1's handlers — nothing bound later can swallow it.
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Tab') return;
      const panel = fuDrawer();
      if (!panel || !panel.classList.contains('is-open')) return;

      const nodes = Array.prototype.filter.call(
        panel.querySelectorAll(FU_DRAWER_FOCUSABLE),
        fuIsFocusableInDrawer
      );
      if (!nodes.length) {
        e.preventDefault();
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      const inside = !!active && typeof panel.contains === 'function' && panel.contains(active);

      if (e.shiftKey) {
        // Shift+Tab off the front, or from anywhere outside: wrap to the end.
        if (!inside || active === first) {
          e.preventDefault();
          fuFocusQuiet(last);
        }
      } else if (!inside || active === last) {
        // Tab off the end, or from anywhere outside: wrap to the front.
        e.preventDefault();
        fuFocusQuiet(first);
      }
    },
    true
  );

  // --- 3. Header search ------------------------------------------------------
  // The <form action="/shop/" method="get"> + <input name="q"> submits natively
  // on Enter. We additionally auto-navigate after 250ms idle (§6.5 debounce).
  const searchForm = document.querySelector('[data-fu-search-form]');
  const searchInput = document.querySelector('[data-fu-search-input]');
  if (searchForm && searchInput) {
    let searchTimer;
    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      const v = searchInput.value.trim();
      if (!v) return;
      searchTimer = setTimeout(() => {
        const action = searchForm.getAttribute('action') || '/shop/';
        const sep = action.includes('?') ? '&' : '?';
        window.location.href = action + sep + 'q=' + encodeURIComponent(v);
      }, 250);
    });
  }

  // --- 4. Shop client-side filter -------------------------------------------
  // Scope every shop query to the Shop page's grid + filter blocks so an
  // unrelated product row on the same page (e.g. a best-sellers block) can't
  // inflate the visible/total count or cross-wire the chips. Each scope falls
  // back to `document` when its block isn't present, so behaviour on the
  // current Shop page stays identical.
  const gridScope =
    document.querySelector('[data-fu-shop-grid]') ||
    document.querySelector('.wp-block-ai-zippy-shop-product-grid') ||
    document;
  const filterScope =
    document.querySelector('[data-fu-shop-filters]') ||
    document.querySelector('.wp-block-ai-zippy-shop-filters') ||
    document;

  const cards = Array.from(gridScope.querySelectorAll('[data-fu-product]'));
  // Whether the address bar may be written at all. The home page's best-sellers
  // block also emits [data-fu-product] cards but has no chips, and `filterScope`
  // falls back to `document` there — so `cards.length` on its own would have us
  // pushing ?cat= onto "/".
  const hasFilters = Boolean(filterScope.querySelector('[data-fu-filter]'));
  if (cards.length) {
    const total = cards.length;
    const countEl = gridScope.querySelector('[data-fu-shop-count]') ||
      document.querySelector('[data-fu-shop-count]');
    const emptyEl = gridScope.querySelector('[data-fu-shop-empty]') ||
      document.querySelector('[data-fu-shop-empty]');
    // The ?q= term is refreshed by readUrlState() below every time the address
    // bar changes, so it cannot be captured once at boot the way it used to be.
    let query = '';

    // The result label is editable via the grid block's `resultLabelFormat`
    // attribute, emitted as `data-count-format` on the count element. Honour it
    // (substituting {visible}/{total}) and FALL BACK to the original hardcoded
    // string when the attribute is absent, so the count works either way.
    const countFormat =
      (countEl && countEl.getAttribute('data-count-format')) ||
      '{visible} of {total} products';

    const isInput = (el) => el.tagName === 'INPUT';
    const isSelected = (el) => (isInput(el) ? el.checked : el.classList.contains('is-active'));
    const valueOf = (el) =>
      (el.getAttribute('data-value') || el.value || el.textContent || '').trim();

    // --- Slug vocabulary ----------------------------------------------------
    // MUST stay identical to familyu_filter_slug() in shop-filters/render.php
    // and shared-primary-nav/render.php: lowercase → every run of
    // non-alphanumerics collapses to one dash → trim dashes. The server prints
    // the result on each control as `data-slug`, so fuSlug() is normally only a
    // fallback for markup served from a cache that predates that attribute.
    const fuSlug = (value) =>
      String(value === null || value === undefined ? '' : value)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    const chipsOf = (kind) =>
      Array.from(filterScope.querySelectorAll(`[data-fu-filter="${kind}"]`));

    const slugOf = (el) => {
      const explicit = (el.getAttribute('data-slug') || '').trim();
      return explicit ? fuSlug(explicit) : fuSlug(valueOf(el));
    };

    // Slug first, then the raw label, so both ?cat=breast-pump and a hand-edited
    // ?cat=Breast%20Pump resolve to the same chip.
    const findBySlug = (kind, wanted) => {
      const slug = fuSlug(wanted);
      if (!slug) return null;
      const group = chipsOf(kind);
      return (
        group.find((el) => slugOf(el) === slug) ||
        group.find((el) => fuSlug(valueOf(el)) === slug) ||
        null
      );
    };

    // The "All" chip, i.e. "no filter" — it carries data-value="All" /
    // data-slug="all" so the slug lookup finds it like any other.
    const allChip = (kind) => findBySlug(kind, 'all');

    // The pressed CLASS and the pressed STATE move together. The server renders
    // both; before this a click moved `is-active` and left aria-pressed pointing
    // at the old answer, so a screen-reader user heard "All, pressed" while
    // looking at a Bags-only grid.
    const setPressed = (el, on) => {
      if (!el) return;
      const pressed = Boolean(on);
      if (isInput(el)) {
        el.checked = pressed;
        return;
      }
      el.classList.toggle('is-active', pressed);
      // Only where the attribute already exists: inventing aria-pressed on a
      // control whose role we don't own would be a claim, not a description.
      if (el.hasAttribute('aria-pressed')) {
        el.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      }
    };

    const activeValue = (kind) => {
      const el = chipsOf(kind).find(isSelected);
      return el ? valueOf(el) : 'All';
    };
    const activeBrands = () =>
      chipsOf('brand')
        .filter(isSelected)
        .map((el) => valueOf(el).toLowerCase())
        .filter(Boolean);

    const apply = () => {
      const cat = activeValue('cat').toLowerCase();
      const stage = activeValue('stage').toLowerCase();
      const brands = activeBrands();
      let visible = 0;

      cards.forEach((card) => {
        const cCat = (card.getAttribute('data-cat') || '').trim().toLowerCase();
        const cStage = (card.getAttribute('data-stage') || '').trim().toLowerCase();
        const cBrand = (card.getAttribute('data-brand') || '').trim().toLowerCase();
        const cName = (card.getAttribute('data-name') || '').trim().toLowerCase();

        const matchQ = !query || cName.includes(query) || cBrand.includes(query);
        const matchCat = cat === 'all' || cCat === cat;
        const matchStage = stage === 'all' || cStage === stage;
        const matchBrand = brands.length === 0 || brands.includes(cBrand);

        const show = matchQ && matchCat && matchStage && matchBrand;
        card.classList.toggle('is-hidden', !show);
        if (show) visible++;
      });

      if (countEl) {
        countEl.textContent = countFormat
          .replace('{visible}', String(visible))
          .replace('{total}', String(total));
      }
      if (emptyEl) emptyEl.hidden = visible !== 0;
    };

    // --- URL <-> chip sync --------------------------------------------------
    // A category link (the Shop mega-menu, or any other /shop/?cat=<slug> entry
    // point) has to land on an already-filtered page, and clicking a chip has to
    // make the address bar shareable — without a reload, and without losing the
    // ?q= term the header search put there.
    const readUrlState = () => {
      const params = new URLSearchParams(window.location.search);
      const one = (name) => fuSlug(params.get(name) || '');
      // Refreshed here rather than captured at boot: a back/forward step can land
      // on a different ?q=, and the grid must still be filtered by that term.
      query = (params.get('q') || '').trim().toLowerCase();
      return {
        cat: one('cat'),
        stage: one('stage'),
        // Multi-select, so a comma list: ?brand=spectra,medela.
        brands: (params.get('brand') || '')
          .split(',')
          .map((token) => fuSlug(token))
          .filter(Boolean)
      };
    };

    // Move the chips to match the URL. Returns true when the URL named a slug no
    // chip owns, so the caller can normalise the address bar back to something
    // honest instead of leaving ?cat=nope sitting above a full grid.
    //
    // `authoritative` says whether the URL is the ONLY source of truth. It is
    // false on first paint, where shop-filters/render.php has already resolved
    // the params into the markup, so a param the URL does not carry must leave
    // that group exactly as rendered. On popstate it is true: the chips are
    // still holding the previous history entry's answer, so a missing param has
    // to put the group back to "All" / unchecked — otherwise stepping Back from
    // ?cat=toys to a clean /shop/ would leave a Toys-only grid on screen.
    const applyUrlToChips = (state, authoritative) => {
      let unknown = false;

      ['cat', 'stage'].forEach((kind) => {
        const wanted = state[kind];
        const match = wanted ? findBySlug(kind, wanted) : null;
        if (wanted && !match) unknown = true;
        // An unrecognisable slug falls back to "All" (every product) rather than
        // to an empty grid the shopper has no way to explain.
        const target = match || (authoritative ? allChip(kind) : null);
        if (!target) return;
        chipsOf(kind).forEach((el) => setPressed(el, el === target));
      });

      if (state.brands.length || authoritative) {
        const matched = [];
        state.brands.forEach((slug) => {
          const el = findBySlug('brand', slug);
          if (el) matched.push(el);
          else unknown = true;
        });
        chipsOf('brand').forEach((el) => setPressed(el, matched.indexOf(el) !== -1));
      }

      return unknown;
    };

    // 'push' → a history entry Back can return to (a chip click, Clear).
    // 'replace' → fix the current entry without adding one (an unknown slug).
    const syncUrl = (mode) => {
      if (!hasFilters) return;
      const method = mode === 'replace' ? 'replaceState' : 'pushState';
      if (!window.history || typeof window.history[method] !== 'function') return;

      try {
        const url = new URL(window.location.href);
        // "All" means "no filter", so it must never appear in the URL — that is
        // precisely what makes Clear hand back a clean, shareable /shop/.
        const setOrDrop = (name, value) => {
          if (!value || value === 'all') url.searchParams.delete(name);
          else url.searchParams.set(name, value);
        };
        setOrDrop('cat', fuSlug(activeValue('cat')));
        setOrDrop('stage', fuSlug(activeValue('stage')));
        setOrDrop(
          'brand',
          chipsOf('brand')
            .filter(isSelected)
            .map(slugOf)
            .filter(Boolean)
            .join(',')
        );

        // `q` is deliberately left alone: a filter click neither invents nor
        // discards the shopper's search term. The path is written relative on
        // purpose — same origin, and it keeps whatever base the site is on.
        // URLSearchParams percent-encodes the multi-brand separator, so put the
        // comma back: it is a sub-delim that RFC 3986 allows bare in a query, and
        // ?brand=spectra,medela is a URL worth sharing where %2C is not.
        const search = url.searchParams.toString().replace(/%2C/g, ',');
        window.history[method](
          window.history.state,
          '',
          url.pathname + (search ? `?${search}` : '') + url.hash
        );
      } catch (err) {
        // A locked-down embed or an engine without URL: the filters keep working,
        // the address bar simply doesn't follow.
      }
    };

    // Category + stage checks — single select behind a checkbox UI. The
    // browser has already flipped `checked` when 'change' fires, so checking
    // an option exclusive-selects it; unchecking the active one (or "All")
    // falls back to "All" checked so the DOM always mirrors the single-select
    // state instead of showing an empty group.
    chipsOf('cat')
      .concat(chipsOf('stage'))
      .forEach((chip) => {
        chip.addEventListener(isInput(chip) ? 'change' : 'click', () => {
          const kind = chip.getAttribute('data-fu-filter');
          if (isInput(chip) && !chip.checked) {
            chipsOf(kind).forEach((el) => setPressed(el, false));
            setPressed(allChip(kind), true);
          } else {
            chipsOf(kind).forEach((el) => setPressed(el, el === chip));
          }
          apply();
          syncUrl('push');
        });
      });

    // Brand filters — multi select (checkbox inputs or toggle chips).
    chipsOf('brand').forEach((el) => {
      el.addEventListener(isInput(el) ? 'change' : 'click', () => {
        if (!isInput(el)) setPressed(el, !isSelected(el));
        apply();
        syncUrl('push');
      });
    });

    // Clear — resets cat / stage / brand (does NOT clear the ?q= query).
    const clearBtn = filterScope.querySelector('[data-fu-filter-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        chipsOf('cat')
          .concat(chipsOf('stage'))
          .concat(chipsOf('brand'))
          .forEach((el) => setPressed(el, false));
        setPressed(allChip('cat'), true);
        setPressed(allChip('stage'), true);
        apply();
        // Every product is back, so the URL has to say so too: leaving ?cat=bags
        // up there would silently re-filter on the next reload or share.
        syncUrl('push');
      });
    }

    // Back / forward: the address bar is the source of truth, so read it and
    // repaint — writing nothing unless it held a slug we could not recognise.
    if (hasFilters) {
      window.addEventListener('popstate', () => {
        // `true`: on a history step the URL, not the markup, decides the state.
        if (applyUrlToChips(readUrlState(), true)) syncUrl('replace');
        apply();
      });
    }

    // First paint. shop-filters/render.php has already pressed the chip matching
    // ?cat= server-side, so applyUrlToChips() is normally a no-op here — it
    // covers markup from a page cache predating that, keeps `query` in step, and
    // turns an unrecognisable ?cat=nope into All (all 26 products) rather than an
    // empty grid.
    const bootUnknown = hasFilters && applyUrlToChips(readUrlState(), false);
    apply();
    if (bootUnknown) syncUrl('replace');
  }

  // --- 5. Cart feedback (Task #24 "Option C" hybrid; single-path since #27) --
  // Normalise the badge from the server-rendered count on first paint, then
  // keep the live region element mounted so later announcements are heard.
  const initialBadge = fuBadges()[0];
  if (initialBadge) fuSetCartCount(fuReadCartCount(initialBadge));
  fuEnsureToast();

  // 5a. Demo controls — honest explanation instead of a dead /cart/ navigation.
  // Delegated on document so cards revealed/hidden by the shop filter (and any
  // future client-rendered grid) are covered without rebinding.
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-fu-demo-cart]')) return;
    e.preventDefault();
    fuShowToast(FU_DEMO_CART_MESSAGE, { tone: 'warn' });
  });

  // 5b. Real adds. The parent theme's Store API path is now the ONLY writer
  // (see inc/block-helpers.php), and it republishes the resulting cart on
  // `wc-blocks_added_to_cart`. Setting the badge from `items_count` is exactly
  // what makes one click equal +1 rather than +2: there is no increment left to
  // double, and a repeated event can only re-assert the server's own number.
  //
  // NO CHILD TOAST HERE (review #2). `add-to-cart.js` calls `showToast()` right
  // AFTER `updateMiniCart()` dispatches this event, so showing ours too meant
  // two notifications per add — and ours (`z-index: 90`) was completely covered
  // by the parent's (`z-index: 99999`, near-full-width below 480px) at 390px.
  // The parent's toast is now the single notification: it is re-skinned to
  // FamilyU in `src/scss/_toast-parent.scss` and augmented with our "View cart"
  // link by 5e below.
  document.body.addEventListener('wc-blocks_added_to_cart', (e) => {
    fuLastStoreAddAt = Date.now();
    const count = fuCartCountFromPayload(e && e.detail ? e.detail.cart : null);
    if (count === null) fuBumpCartCount(1);
    else fuSetCartCount(count);
  });

  // 5c. Fallback for a classic WooCommerce add this theme does not own (e.g. a
  // core button on a single-product page). Skipped while the Store API write
  // above is still fresh, since the parent fires both events for one add.
  fuOnWcAddedToCart((event, fragments, cartHash, $button) => {
    if (fuStoreAddJustHandled()) return;
    fuBumpCartCount(fuQuantityFrom($button));
    // Still one notification: if the parent is already showing a toast for this
    // add, stay quiet. And if the parent's toast lands a beat later, 5e hides
    // ours the moment it appears — so this path can never end up with two.
    if (fuParentToastActive()) return;
    fuShowAddedToast();
  });

  // 5d. Every other cart mutation — remove, quantity change, clear — happens in
  // the parent's React /cart/ app, which republishes the cart here. Mirroring it
  // keeps the header badge from contradicting the cart the user is looking at.
  window.addEventListener('ai-zippy-cart-updated', (e) => {
    const count = fuCartCountFromPayload(e && e.detail);
    if (count !== null) fuSetCartCount(count);
  });

  // 5e. Adopt the parent theme's toast (review #2).
  //
  // `toast.js` builds `div.az-toast.az-toast--{type}` as nodes and appends it
  // straight to document.body:
  //   <span>{message}</span>
  //   <button class="az-toast__close" aria-label="Close">&times;</button>
  //   <span class="az-toast__bar" aria-hidden="true"></span>
  // It has no cart affordance and no live region, so we add both — that is the
  // whole of our involvement. All the LOOK comes from _toast-parent.scss.
  //
  // A MutationObserver rather than a microtask/timeout: the parent calls
  // `showToast()` AFTER `updateMiniCart()` dispatches `wc-blocks_added_to_cart`,
  // so the node does not exist while our 5b handler runs, and "wait one tick"
  // would silently break if the parent ever awaited between the two calls.
  // `childList` on body alone is enough (no `subtree`), so this costs nothing on
  // an ordinary page.
  const fuAugmentParentToast = (toast) => {
    if (!(toast instanceof Element)) return;
    if (toast.getAttribute('data-fu-augmented') === '1') return;
    toast.setAttribute('data-fu-augmented', '1');

    const isError = toast.classList.contains('az-toast--error');

    // The parent toast is announced by nothing at all. Suppressing our own
    // success toast must not cost screen-reader users the confirmation, so give
    // the node a polite live region (errors are left to the parent's own
    // handling rather than being made assertive behind its back).
    if (!isError && !toast.hasAttribute('role')) {
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
    }

    // "View cart" only makes sense on a success toast — error toasts come from
    // the Store API failure path and from the mini cart.
    if (isError) return;
    if (!toast.classList.contains('az-toast--success')) return;
    if (toast.querySelector('.fu-toast__cart')) return;

    const link = document.createElement('a');
    link.className = 'fu-toast__cart';
    link.href = fuCartUrl();
    link.textContent = FU_VIEW_CART_LABEL;

    // Before the close button, so the reading/Tab order is
    // message → View cart → close.
    const close = toast.querySelector('.az-toast__close');
    if (close && close.parentNode === toast) toast.insertBefore(link, close);
    else toast.appendChild(link);
  };

  const fuObserveParentToasts = () => {
    // Anything already on the page before we started watching.
    document.querySelectorAll(FU_PARENT_TOAST).forEach(fuAugmentParentToast);

    if (typeof MutationObserver !== 'function' || !document.body) return;

    const observer = new MutationObserver((records) => {
      let appeared = false;

      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          const isToast = node.matches(FU_PARENT_TOAST);
          if (!isToast && !node.querySelector(FU_PARENT_TOAST)) return;
          appeared = true;
          // Only stamp data-fu-augmented on an actual toast. A wrapper node that
          // merely CONTAINS one is handled by the querySelectorAll below, and
          // marking it would put our bookkeeping attribute on unrelated markup.
          if (isToast) fuAugmentParentToast(node);
          node.querySelectorAll(FU_PARENT_TOAST).forEach(fuAugmentParentToast);
        });
      });

      // One visible notification at a time. If a demo toast from 5a was still up
      // when the user clicked a real product, retire it — the parent's toast is
      // the one that matters, and the two would otherwise overlap at the bottom
      // of a 390px viewport.
      if (appeared) fuHideToast();
    });

    observer.observe(document.body, { childList: true });
  };

  fuObserveParentToasts();
});
